import React, { useState, useRef, useEffect } from "react";
import { Send, User2, ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";

interface ChatInputProps {
  onSendMessage: (message: string, personality: string | null) => void;
  isLoading: boolean;
  isEmpty: boolean;
  value: string;
  onInputChange: (value: string) => void;
}

const personalities = [
 
  {
    id: "tom",
    value: "Tom",
    icon: "🎓",
    label: "Professor",
    description: "A transcendent polymath with deep analytical abilities",
  },
  // {
  //   id: "sarah",
  //   value: "Sarah",
  //   icon: "⚖️",
  //   label: "Legal Expert",
  //   description: "Specialized in legal analysis and consultation",
  // },
  // {
  //   id: "alex",
  //   value: "Alex",
  //   icon: "🤖",
  //   label: "AI Assistant",
  //   description: "Neutral and objective AI assistance",
  // },
];

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  isLoading,
  isEmpty,
  value,
  onInputChange,
}) => {
  const [selectedPersonality, setSelectedPersonality] = useState<string | null>(
    null
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const newHeight = Math.min(textarea.scrollHeight, 150);
      textarea.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [value]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !isLoading) {
      onSendMessage(value.trim(), selectedPersonality);
      // onInputChange(""); // Parent should clear it
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const selectedPerson =
    personalities.find((p) => p.value === selectedPersonality) ||
    personalities[0];

  return (
    <form
      onSubmit={handleSubmit}
      className={`p-2 sm:p-4 bg-white ${isEmpty ? "" : "border-t"}`}
    >
      <div
        className={`max-w-4xl mx-auto ${
          isEmpty ? "bg-green-50 rounded-lg shadow-lg p-3 sm:p-4" : ""
        }`}
      >
        <div className="flex flex-col sm:flex-row gap-2 w-full">
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors self-start"
              >
                <span className="text-xl">{selectedPerson.icon}</span>
                <span className="hidden sm:inline">{selectedPerson.label}</span>
                <ChevronDown className="w-4 h-4 text-gray-500 hidden sm:block" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2">
              <div className="space-y-1 border rounded-lg bg-white shadow-md">
                {personalities.map((personality) => (
                  <button
                    key={personality.id}
                    type="button"
                    onClick={() => {
                      setSelectedPersonality(personality.value);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                      selectedPersonality === personality.value
                        ? "bg-green-100 text-green-900"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <span className="text-xl">{personality.icon}</span>
                    <div className="text-left">
                      <div className="font-medium">{personality.label}</div>
                      <div className="text-xs text-gray-500">
                        {personality.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <div className="flex-1 flex gap-2 w-full">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about the tax reform bill..."
              className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none min-h-[44px] max-h-[150px] text-base sm:text-sm overflow-y-auto w-full"
              disabled={isLoading}
              rows={1}
            />
            <button
              type="submit"
              disabled={isLoading || !value.trim()}
              className="px-3 py-2 sm:px-4 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 h-[44px]"
            >
              <Send className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ChatInput;
